import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClientQueriesThunk } from '../features/queries/queryThunks';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Component/Sidebar';

const ClientQueriesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { queries, loading, error, next, count } = useSelector((state) => state.queries);
  const { user } = useSelector((state) => state.auth);
  
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    query_type: '',
    search: ''
  });
  
  const limit = 20;

  // Check if user is an employee
  const isEmployee = user?.employee_profile?.id;

  useEffect(() => {
    if (isEmployee) {
      dispatch(fetchClientQueriesThunk({ limit, offset: 0 }));
    }
  }, [dispatch, isEmployee]);

  const handleLoadMore = () => {
    if (next) {
      const offset = (page + 1) * limit;
      dispatch(fetchClientQueriesThunk({ offset, limit }));
      setPage(page + 1);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    setPage(0);
    const filterParams = {
      limit,
      offset: 0,
      ...filters
    };
    dispatch(fetchClientQueriesThunk(filterParams));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      query_type: '',
      search: ''
    });
    setPage(0);
    dispatch(fetchClientQueriesThunk({ limit, offset: 0 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const capitalizeText = (text) => {
    if (!text) return '';
    return text.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return 'client-query-status-default';
    const statusLower = status.toLowerCase().replace(/\s+/g, '-');
    return `client-query-status-${statusLower}`;
  };

  const getQueryTypeClass = (queryType) => {
    if (!queryType) return 'client-query-type-default';
    return `client-query-type-${queryType}`;
  };

  if (!isEmployee) {
    return (
      <div className="client-queries-container">
        <div className="client-queries-error">
          <h2>Access Denied</h2>
          <p>Only employees can access client queries.</p>
        </div>
      </div>
    );
  }

  return (
        <div className='index-box'>
      <Sidebar />
    <div className="client-queries-container">
      {/* Header */}
      <div className="client-queries-header">
        <h1>Client Queries</h1>
        <button 
          className="client-queries-create-btn"
          onClick={() => navigate('/client-queries/new')}
        >
          + Create Query
        </button>
      </div>

      {/* Filters */}
      <div className="client-queries-filters">
        <div className="client-queries-filter-row">
          <div className="client-queries-filter-group">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by subject or store name..."
              className="client-queries-search-input"
            />
          </div>
          
          <div className="client-queries-filter-group">
            <select
              name="query_type"
              value={filters.query_type}
              onChange={handleFilterChange}
              className="client-queries-filter-select"
            >
              <option value="">All Types</option>
              <option value="incident">Incident</option>
              <option value="assign_task">Assign Task</option>
            </select>
          </div>

          <div className="client-queries-filter-group">
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="client-queries-filter-select"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="client-queries-filter-actions">
            <button 
              className="client-queries-filter-btn client-queries-apply-btn"
              onClick={handleFilterApply}
              disabled={loading}
            >
              Apply
            </button>
            <button 
              className="client-queries-filter-btn client-queries-clear-btn"
              onClick={handleClearFilters}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="client-queries-content">
        {loading && queries.length === 0 ? (
          <div className="client-queries-loading">
            <div className="client-queries-loader"></div>
            <p>Loading client queries...</p>
          </div>
        ) : error ? (
          <div className="client-queries-error">
            <h3>Error Loading Queries</h3>
            <p>{error}</p>
            <button 
              className="client-queries-retry-btn"
              onClick={() => dispatch(fetchClientQueriesThunk({ limit, offset: 0 }))}
            >
              Retry
            </button>
          </div>
        ) : queries.length === 0 ? (
          <div className="client-queries-empty">
            <div className="client-queries-empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>No Client Queries Found</h3>
            <p>There are no client queries to display. Create your first query to get started.</p>
            <button 
              className="client-queries-create-first-btn"
              onClick={() => console.log('Navigate to create query')}
            >
              Create First Query
            </button>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="client-queries-stats">
              <div className="client-queries-stat-item">
                <span className="client-queries-stat-number">{count}</span>
                <span className="client-queries-stat-label">Total Queries</span>
              </div>
            </div>

            {/* Table */}
            <div className="client-queries-table-container">
              <div className="client-queries-table-header">
                <div className="client-queries-table-cell client-queries-col-store">Store</div>
                <div className="client-queries-table-cell client-queries-col-type">Type</div>
                <div className="client-queries-table-cell client-queries-col-subject">Subject</div>
                <div className="client-queries-table-cell client-queries-col-status">Status</div>
                <div className="client-queries-table-cell client-queries-col-date">Date</div>
                <div className="client-queries-table-cell client-queries-col-actions">Actions</div>
              </div>

              <div className="client-queries-table-body">
                {queries.map((query) => (
                  <div key={query.id} className="client-queries-table-row">
                    <div className="client-queries-table-cell client-queries-col-store">
                      <div className="client-queries-store-info">
                        {query.store?.image ? (
                          <img 
                            src={query.store.image} 
                            alt={query.store.store_name || 'Store'} 
                            className="client-queries-store-avatar"
                          />
                        ) : (
                          <div className="client-queries-store-avatar client-queries-avatar-default">
                            {query.store?.store_name?.[0] || '?'}
                          </div>
                        )}
                        <div className="client-queries-store-details">
                          <span className="client-queries-store-name">
                            {query.store?.store_name || 'Unknown Store'}
                          </span>
                          <span className="client-queries-store-city">
                            {query.store?.store_city || ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="client-queries-table-cell client-queries-col-type">
                      <span className={`client-queries-type-badge ${getQueryTypeClass(query.query_type)}`}>
                        {capitalizeText(query.query_type)}
                      </span>
                    </div>

                    <div className="client-queries-table-cell client-queries-col-subject">
                      <div className="client-queries-subject-info">
                        <span className="client-queries-subject-text">{query.subject}</span>
                        {query.details && (
                          <span className="client-queries-details-preview">
                            {query.details.length > 60 
                              ? `${query.details.substring(0, 60)}...` 
                              : query.details
                            }
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="client-queries-table-cell client-queries-col-status">
                      <span className={`client-queries-status-badge ${getStatusBadgeClass(query.status)}`}>
                        {capitalizeText(query.status) || 'No Status'}
                      </span>
                    </div>

                    <div className="client-queries-table-cell client-queries-col-date">
                      <div className="client-queries-date-info">
                        <span className="client-queries-created-date">
                          {formatDate(query.created_at)}
                        </span>
                        {query.incident_date && (
                          <span className="client-queries-incident-date">
                            Incident: {formatDate(query.incident_date)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="client-queries-table-cell client-queries-col-actions">
                      <div className="client-queries-actions">
                      <button
                          className="client-queries-action-btn client-queries-edit-btn"
                          onClick={() => navigate(`/client-queries/${query.id}/edit`)}
                          title="Edit Query"
                        >
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Load More */}
            {next && (
              <div className="client-queries-load-more">
                <button 
                  className="client-queries-load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More Queries'}
                </button>
              </div>
            )}

            {loading && queries.length > 0 && (
              <div className="client-queries-loading-more">
                <div className="client-queries-loader-small"></div>
                <span>Loading more queries...</span>
              </div>
            )}
          </>
        )}
      </div>


      <style jsx>{`
        .client-queries-container {
          padding: 24px;
          background: #fafbfc;
          min-height: 100vh;
          width: 80%
        }

        .client-queries-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .client-queries-header h1 {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .client-queries-create-btn {
          background: #f43f5e;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .client-queries-create-btn:hover {
          background: #f41f42ff;
          transform: translateY(-1px);
        }

        .client-queries-filters {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .client-queries-filter-row {
          display: flex;
          gap: 16px;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .client-queries-filter-group {
          display: flex;
          flex-direction: column;
          min-width: 200px;
        }

        .client-queries-search-input,
        .client-queries-filter-select {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: border-color 0.2s;
        }

        .client-queries-search-input:focus,
        .client-queries-filter-select:focus {
          outline: none;
          border-color: #f43f5e;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .client-queries-filter-actions {
          display: flex;
          gap: 8px;
          align-items: flex-end;
        }

        .client-queries-filter-btn {
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .client-queries-apply-btn {
          background: #f43f5e;
          color: white;
        }

        .client-queries-apply-btn:hover:not(:disabled) {
          background: #2563eb;
        }

        .client-queries-clear-btn {
          background: #6b7280;
          color: white;
        }

        .client-queries-clear-btn:hover:not(:disabled) {
          background: #4b5563;
        }

        .client-queries-filter-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .client-queries-content {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .client-queries-loading,
        .client-queries-error,
        .client-queries-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .client-queries-loader {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #f43f5e;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .client-queries-empty-icon {
          margin-bottom: 16px;
          color: #9ca3af;
        }

        .client-queries-empty h3 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
          color: #374151;
        }

        .client-queries-empty p {
          margin: 0 0 20px 0;
          color: #6b7280;
          max-width: 400px;
          line-height: 1.5;
        }

        .client-queries-create-first-btn,
        .client-queries-retry-btn {
          background: #f43f5e;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-queries-create-first-btn:hover,
        .client-queries-retry-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .client-queries-stats {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }

        .client-queries-stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .client-queries-stat-number {
          font-size: 28px;
          font-weight: 700;
          color: #1f2937;
        }

        .client-queries-stat-label {
          font-size: 14px;
          color: #6b7280;
          margin-top: 4px;
        }

        .client-queries-table-container {
          overflow-x: auto;
        }

        .client-queries-table-header {
          display: flex;
          background: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
        }

        .client-queries-table-cell {
          padding: 16px;
          font-size: 14px;
          display: flex;
          align-items: center;
        }

        .client-queries-table-header .client-queries-table-cell {
          font-weight: 600;
          color: #374151;
          background: #f9fafb;
        }

        .client-queries-col-store { width: 200px; min-width: 200px; }
        .client-queries-col-type { width: 120px; min-width: 120px; }
        .client-queries-col-subject { width: 300px; min-width: 300px; }
        .client-queries-col-status { width: 120px; min-width: 120px; }
        .client-queries-col-date { width: 150px; min-width: 150px; }
        .client-queries-col-actions { width: 100px; min-width: 100px; justify-content: center; }

        .client-queries-table-body {
          background: white;
        }

        .client-queries-table-row {
          display: flex;
          border-bottom: 1px solid #f3f4f6;
          transition: background-color 0.2s;
        }

        .client-queries-table-row:hover {
          background: #f8fafc;
        }

        .client-queries-store-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .client-queries-store-avatar {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          object-fit: cover;
        }

        .client-queries-avatar-default {
          background: #f43f5e;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .client-queries-store-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .client-queries-store-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .client-queries-store-city {
          font-size: 12px;
          color: #6b7280;
        }

        .client-queries-type-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .client-queries-type-incident {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .client-queries-type-assign_task {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #f43f5e;
        }

        .client-queries-type-default {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }

        .client-queries-subject-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .client-queries-subject-text {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .client-queries-details-preview {
          font-size: 12px;
          color: #6b7280;
          line-height: 1.4;
        }

        .client-queries-status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .client-queries-status-pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .client-queries-status-in-progress {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #f43f5e;
        }

        .client-queries-status-completed {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #10b981;
        }

        .client-queries-status-cancelled {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .client-queries-status-default {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }

        .client-queries-date-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .client-queries-created-date {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .client-queries-incident-date {
          font-size: 12px;
          color: #6b7280;
        }

        .client-queries-actions {
          display: flex;
          gap: 8px;
        }

        .client-queries-action-btn {
          padding: 6px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .client-queries-view-btn {
          background: #dbeafe;
          color: #1e40af;
        }

        .client-queries-view-btn:hover {
          background: #bfdbfe;
        }

        .client-queries-edit-btn {
          background: #fef3c7;
          color: #92400e;
        }

        .client-queries-edit-btn:hover {
          background: #fde68a;
        }

        .client-queries-load-more {
          padding: 24px;
          text-align: center;
          border-top: 1px solid #e5e7eb;
        }

        .client-queries-load-more-btn {
          background: white;
          color: #f43f5e;
          border: 1px solid #f43f5e;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-queries-load-more-btn:hover:not(:disabled) {
          background: #f43f5e;
          color: white;
        }

        .client-queries-load-more-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .client-queries-loading-more {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #e5e7eb;
          background: #f8fafc;
        }

        .client-queries-loader-small {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid #f43f5e;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .client-queries-loading-more span {
          font-size: 14px;
          color: #6b7280;
        }

        @media (max-width: 768px) {
          .client-queries-container {
            padding: 16px;
          }

          .client-queries-filter-row {
            flex-direction: column;
            align-items: stretch;
          }

          .client-queries-filter-group {
            min-width: auto;
          }

          .client-queries-filter-actions {
            flex-direction: row;
            align-items: center;
          }

          .client-queries-table-container {
            font-size: 12px;
          }

          .client-queries-col-store,
          .client-queries-col-type,
          .client-queries-col-subject,
          .client-queries-col-status,
          .client-queries-col-date,
          .client-queries-col-actions {
            min-width: 120px;
          }
        }
      `}</style>
    </div>
    </div>
  );
};

export default ClientQueriesPage;